import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToSection } from '../utils/scrollToSection';

export const FixedNav = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('home');
  const [hoveredItem, setHoveredItem] = useState(null);
    const isProject = activeSection === 'projects';

  const navItems = [
    { id: 'home', label: 'HOME', type: 'scroll' },
    { id: 'about', label: 'ABOUT', type: 'scroll' },
    { id: 'projects', label: 'PROJECTS', type: 'scroll' },
    { id: 'stories', label: 'STORIES', type: 'scroll' },
    { id: 'contact', label: 'CONTACT', type: 'scroll' }
  ];

  useEffect(() => {
    let rafId = null;

    const updateActiveSection = () => {
      rafId = null;
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateActiveSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateActiveSection();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll sections (home/about/projects/stories/contact) only exist on '/'.
  // When we land on '/' with a hash (e.g. navigated here from /cv), scroll to it.
  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;

    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      scrollToSection(id);
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  // Full-height strip on the right edge: only the links themselves take
  // clicks, so it never blocks content underneath.
  return (
    <nav aria-label="Sections" className="fixed right-0 top-0 bottom-0 z-50 hidden md:flex items-center pointer-events-none">
      <div className="relative h-full flex items-center pr-4">
        {/* Vertical line - more visible */}
        <div className={`absolute right-3 top-1/2 -translate-y-1/2 w-[2px] h-[50%] transition-colors duration-500 ${isProject ? 'bg-white/20' : 'bg-charcoal/20'}`}></div>

        {/* Nav items */}
      <div className="relative flex flex-col justify-center gap-12">
  {navItems.map((item, index) => {
    const isActive = location.pathname === '/' && activeSection === item.id;
    const isHovered = hoveredItem === item.id;

    // Helper to determine color based on state
    const getBaseColor = () => {
      if (isActive) return '#ff6347'; // Active Orange
      if (isProject) return 'white'; // White/Cream for Projects section
      return '#1a1818'; // Default Black
    };

    const color = getBaseColor();

    const itemContent = (
      <>
        {/* Vertical text */}
        <div className="relative overflow-hidden" style={{ width: '14px' }}>
          <div
            className="font-[550] tracking-[4px] transition-all duration-500 ease-out"
            style={{
              writingMode: 'vertical-rl',
              transform: `rotate(180deg) translateX(${isActive || isHovered ? '0px' : '100%'})`,
              fontSize: '10px',
              color: color, // Uses the dynamic color
              opacity: isActive ? 1 : isHovered ? 0.8 : 0
            }}
          >
            {item.label}
          </div>
        </div>

        {/* Dot + Dash indicator */}
        <div className="ml-2 flex items-center gap-1">
          {/* Dot */}
          <div
            className="rounded-full transition-all duration-300 ease-out"
            style={{
              width: isActive ? '6px' : '4px',
              height: isActive ? '6px' : '4px',
              backgroundColor: color, // Uses the dynamic color
              opacity: isActive ? 1 : isHovered ? 0.9 : 0.7
            }}
          ></div>
          {/* Dash */}
          <div
            className="transition-all duration-300 ease-out"
            style={{
              width: isActive ? '24px' : isHovered ? '18px' : '12px',
              height: '2px',
              backgroundColor: color, // Uses the dynamic color
              opacity: isActive ? 1 : isHovered ? 0.8 : 0.5
            }}
          ></div>
        </div>
      </>
    );

    if (item.type !== 'link' && location.pathname !== '/') {
      // Scroll-section link, but we're not on the page that has the sections —
      // route home first, then FixedNav's hash effect scrolls once we land there.
      return (
        <Link
          key={item.id}
          to={`/#${item.id}`}
          onMouseEnter={() => setHoveredItem(item.id)}
          onMouseLeave={() => setHoveredItem(null)}
          className="group relative flex items-center justify-end cursor-pointer pointer-events-auto py-2"
        >
          {itemContent}
        </Link>
      );
    }

    return (
      <a
        key={item.id}
        href={item.type === 'link' ? item.href : `#${item.id}`}
        onClick={item.type === 'link' ? undefined : (e) => {
          e.preventDefault();
          scrollToSection(item.id);
        }}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        className="group relative flex items-center justify-end cursor-pointer pointer-events-auto py-2"
      >
        {itemContent}
      </a>
    );
  })}
</div>
      </div>
    </nav>
  );
};
